import axios from '@fs/zion-axios'

const serviceBase = '/service/discovery'
const commonServiceBase = '/service/discovery/common'

const emptyDataObj = { data: {} }
const emptyDataArray = { data: [] }

export const getChurchHistoryData = async ({ countryCode }) => {
  const response = countryCode
    ? await axios.get(`${serviceBase}/wicf/country/${countryCode}/church-history`)
    : emptyDataArray
  return response.data
}

export const getFamilyGenerationsData = async (includeLocations = false, personId = null) => {
  const encodedPersonId = personId ? encodeURIComponent(personId) : ''
  const personIdQuery = encodedPersonId ? `&personId=${encodedPersonId}` : ''
  const url = `${serviceBase}/wicf/family?generations=8&includeLocations=${includeLocations}${personIdQuery}`
  const response = await axios.get(url)
  return response.data
}

export const getParentsData = async (childId) => {
  if (!childId) return null

  try {
    const response = await axios.get(`${serviceBase}/wicf/parents/${childId}`)
    return response.data
  } catch (error) {
    if (error?.response?.status === 404) {
      return null
    }
    throw error
  }
}

export const getAncestorImageData = async (personId, sessionId) => {
  const response = personId && sessionId ? await axios.get(`${serviceBase}/wicf/person/${personId}/portrait`) : {}
  return response.data
}

export const getCountryEventsData = async (countryCode) => {
  const response = countryCode ? await axios.get(`${serviceBase}/wicf/country/${countryCode}/timeline`) : emptyDataArray
  return response.data
}

export const getCountryFoodFacts = ({ countryCode }) => {
  if (countryCode) return axios.get(`${serviceBase}/wicf/country/${countryCode}/food`)
  return Promise.resolve(emptyDataObj)
}

export const getCountryFamilyFacts = ({ countryCode }) => {
  if (countryCode) return axios.get(`${serviceBase}/wicf/country/${countryCode}/family`)
  return Promise.resolve(emptyDataObj)
}

export const getCountrySocialFacts = ({ countryCode }) => {
  if (countryCode) return axios.get(`${serviceBase}/wicf/country/${countryCode}/social`)
  return Promise.resolve(emptyDataObj)
}

export const getCountryTraditionFacts = async ({ countryCode }) => {
  if (countryCode) return axios.get(`${serviceBase}/wicf/country/${countryCode}/traditions`)
  return Promise.resolve(emptyDataArray)
}

export const getCountryChurchFacts = ({ countryCode }) => {
  if (countryCode) return axios.get(`${serviceBase}/wicf/country/${countryCode}/church-history`)
  return Promise.resolve(emptyDataArray)
}

export const getCountryFacts = ({ countryCode }) => {
  if (countryCode) return axios.get(`${serviceBase}/wicf/country/${countryCode}/facts`)
  return Promise.resolve({ data: { list: [], attribution: '' } })
}

export const getFirstConverts = async (pidList) => {
  const list = pidList.join(',')
  const response = await axios.get(`${serviceBase}/wicf/potential-converts?personIds=${list}`)
  return response.data
}

export const getTimelineData = async () => {
  const response = await axios.all([
    axios.get(`/service/discovery/wicf/event/church`),
    axios.get(`/service/discovery/wicf/event/world`),
  ])
  return response
}

export const getRecipesData = async ({ countryCode }) => {
  const response = countryCode ? await axios.get(`${serviceBase}/wicf/country/${countryCode}/recipes`) : emptyDataArray
  return response.data
}

export const getMemories = async ({ personId }) =>
  axios.get(`${serviceBase}/common/memory/person/${personId}?count=100`)

export const getOrdinances = async ({ personId }) => {
  const response = personId
    ? await axios.get(`${serviceBase}/wicf/ordinances/person/${encodeURIComponent(personId)}`)
    : emptyDataArray
  return response.data
}

export const postUserFeedback = async ({ description, contactApproval, context }) => {
  const data = {
    description,
    contactApproval,
    context,
  }

  const response = await axios.post(`${serviceBase}/wicf/feedback`, data)
  return response.status
}

export const postPhotoMemories = ({ data }) => {
  return axios.post(`${serviceBase}/common/memory/image`, data)
}

export const postStoriesMemories = ({ data }) => {
  return axios.post(`${serviceBase}/common/memory/story`, data)
}

export const postDocumentMemories = ({ ancestorId, data }) => {
  return axios.post(`${serviceBase}/common/memory/story?ancestorId=${ancestorId}`, data)
}

export const postAudioMemories = ({ ancestorId, data }) => {
  return axios.post(`${serviceBase}/common/memory/audio?ancestorId=${ancestorId}`, data)
}

export const postBirthLocation = async ({ updatedPerson }) => {
  const response = axios.post(`${serviceBase}/common/tree/person/birth/location`, updatedPerson)
  return response.status
}

export const deleteMemory = async ({ artifactId }) =>
  artifactId && axios.delete(`${commonServiceBase}/memory/id/${artifactId}`)
